import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_HIGHEST } from './brandDashboardTokens';

const isDev = process.env.NODE_ENV !== 'production';

export interface BrandCampaignStepperProps {
    currentStep: number;
    totalSteps: number;
    stepLabel: string;
    /** Short line under the step title (e.g. wizard page context). */
    stepSubtitle?: string;
    onAutoFill?: () => void;
}

export function BrandCampaignStepper({
    currentStep,
    totalSteps,
    stepLabel,
    stepSubtitle,
    onAutoFill,
}: BrandCampaignStepperProps) {
    return (
        <Box
            id="campaign-stepper"
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 2, sm: 1.5 },
                mb: 4,
                minWidth: 0,
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.18em',
                        color: BRAND_LUXE_PRIMARY,
                    }}>
                    Step {currentStep + 1} of {totalSteps}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            color: '#fff',
                        }}>
                        {stepLabel}
                    </Typography>
                    {isDev && onAutoFill && (
                        <Tooltip title="Dev: auto-fill this step" arrow>
                            <IconButton
                                onClick={onAutoFill}
                                size="small"
                                sx={{
                                    color: BRAND_LUXE_PRIMARY,
                                    bgcolor: 'rgba(255,201,101,0.08)',
                                    border: '1px dashed rgba(255,201,101,0.3)',
                                    '&:hover': { bgcolor: 'rgba(255,201,101,0.18)' },
                                    ml: 0.5,
                                    width: 32,
                                    height: 32,
                                }}>
                                <BoltIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {stepSubtitle ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.55)',
                            maxWidth: 560,
                            lineHeight: 1.45,
                        }}>
                        {stepSubtitle}
                    </Typography>
                ) : null}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-start' }}>
                {Array.from({ length: totalSteps }).map((_, i) => {
                    const isActive = i <= currentStep;
                    const isCurrent = i === currentStep;
                    return (
                        <Box
                            key={i}
                            sx={{
                                height: 6,
                                width: isCurrent ? 48 : 32,
                                borderRadius: 999,
                                bgcolor: isActive ? BRAND_LUXE_PRIMARY : BRAND_SURFACE_CONTAINER_HIGHEST,
                                boxShadow: isCurrent ? '0 0 10px rgba(255,193,7,0.4)' : 'none',
                                transition: 'all 0.3s ease',
                            }}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}
